import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { ArrowRight, Briefcase, GraduationCap, Users, BookOpen, Building2 } from "lucide-react";

const ROLES = [
  { value: "client", label: "Client / Researcher", desc: "Submit projects, track publications, manage research output.", icon: Briefcase },
  { value: "student", label: "Student", desc: "Convert your project/thesis into an impactful research paper.", icon: GraduationCap },
  { value: "mentor", label: "Mentor", desc: "Review submissions, approve milestones, mentor researchers.", icon: Users },
  { value: "tutor", label: "Tutor / LMS Author", desc: "Author courses and learning content for researchers.", icon: BookOpen },
  { value: "partner_institution", label: "Partner Institution", desc: "Manage cohorts, workshops, students, and analytics.", icon: Building2 },
];

export default function OnboardingRole() {
  const navigate = useNavigate();
  const [role, setRole] = useState(sessionStorage.getItem("kb_onboarding_role") || "client");

  useEffect(() => {
    // If user is already logged in, skip
    if (localStorage.getItem("kb_session_token")) {
      navigate("/app", { replace: true });
    }
  }, [navigate]);

  const next = () => {
    sessionStorage.setItem("kb_onboarding_role", role);
    navigate("/onboarding/auth");
  };

  return (
    <div className="min-h-screen bg-kb-bg flex flex-col" data-testid="onboarding-role-page">
      <Navbar />
      <div className="flex-1 max-w-3xl mx-auto w-full px-6 py-14">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-3">STEP 1 OF 3</div>
        <h1 className="font-serif text-4xl lg:text-5xl text-kb-ink leading-tight">
          How will you use <em className="italic text-kb-primary">KhojByte</em>?
        </h1>
        <p className="mt-3 text-kb-ink-muted">Pick the role that best matches you. You can change this later.</p>

        <div className="mt-10 space-y-3" data-testid="role-list">
          {ROLES.map((r) => {
            const active = role === r.value;
            return (
              <button
                key={r.value}
                onClick={() => setRole(r.value)}
                className={`w-full text-left border rounded-sm p-5 flex items-start gap-4 transition-all ${
                  active ? "border-kb-primary bg-kb-primary/5 shadow-sm" : "border-slate-200 hover:border-kb-primary/50"
                }`}
                data-testid={`role-${r.value}`}
              >
                <div className={`w-10 h-10 rounded-sm flex items-center justify-center shrink-0 ${
                  active ? "bg-kb-primary text-white" : "bg-slate-100 text-kb-ink-muted"
                }`}>
                  <r.icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <div className="font-serif text-lg text-kb-ink leading-tight">{r.label}</div>
                  <div className="text-sm text-kb-ink-muted mt-1">{r.desc}</div>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 mt-1 shrink-0 ${
                  active ? "border-kb-primary bg-kb-primary" : "border-slate-300"
                }`}>
                  {active && <div className="w-2 h-2 bg-white rounded-full m-auto mt-1" />}
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-10 flex items-center justify-between">
          <Link to="/" className="kb-mono text-xs text-kb-ink-muted hover:text-kb-primary">← BACK TO HOME</Link>
          <button onClick={next} className="kb-btn-primary" data-testid="onboarding-role-next">
            Continue <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
